<template>
  <div v-if="model" :class="['nota-card', { 'nota-card-owner': model.PuedeEditar }]">
    <div class="nota-card-header">
      <div class="nota-card-author">
        <div class="nota-card-author-avatar">
          {{ inicialesAutor }}
        </div>
        <div class="nota-card-author-name">{{ model.NombreCreador }} {{ model.ApellidosCreador }}</div>
      </div>
      <div class="nota-card-actions">
        <div class="nota-card-created-at">
          {{ formatDate(model.FechaCreacion) }}
        </div>
        <button type="button" v-if="model.PuedeEditar" class="nota-card-btn-edit" @click="editarNota">
          <span class="edit-label">{{ $t("borradores-operacion.ui.buttons.editar_nota") }}</span>
          <i class="dx-icon-edit"></i>
        </button>
      </div>
    </div>

    <div class="nota-card-body">
      {{ model.Contenido }}
    </div>

    <div v-if="model.FechaModificacion" class="nota-card-footer">
      <div class="nota-card-edited-badge">
        <div class="dot"></div>
        {{ $t("borradores-operacion.form.editada") }} &middot; {{ formatDate(model.FechaModificacion) }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DetailNotaBorradorOperacionModel } from "@/.shared/.api";
import { useIntl } from "@/.shared/services/i18n";
import { computed } from "vue";
import { useModalService } from "@/.shared/services/modal-service";
import { useApi } from "@/.shared/.api/integration";
import InputBox from "@/.shared/components/dialogs/InputBox.vue";
import { messageBoxResult } from "@/.shared/components/dialogs/message-box";
import { toastError } from "@/.shared/services/notification-service";

const intl = useIntl();
const modal = useModalService();
const api = useApi();

const props = defineProps({
  model: Object as () => DetailNotaBorradorOperacionModel,
  idBorradorOperacion: {
    type: String,
    required: true,
  },
});

const emit = defineEmits<{
  (e: "update:nota"): void;
}>();

const formatDate = (fecha: Date) => {
  if (!fecha) return "";

  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(fecha));
};

const inicialesAutor = computed(() => {
  if (!props.model) return "";
  return props.model?.NombreCreador.charAt(0).toUpperCase() + props.model?.ApellidosCreador.charAt(0).toUpperCase();
});

const editarNota = async () => {
  let responseModal = (await modal.showModal(
    InputBox,
    {
      label: intl.i18n.t("borradores-operacion.form.nota_label"),
      required: true,
      dxInputType: "dxTextArea",
      dxInputProps: {
        placeholder: intl.i18n.t("borradores-operacion.form.nota_placeholder"),
        inputAttr: {
          rows: 5,
        },
        value: props.model?.Contenido,
      },
    },
    {
      title: undefined,
      width: 500,
      height: 300,
    },
  )) as { result: string; value: string | null };

  if (responseModal.result == messageBoxResult.ok && responseModal.value) {
    try {
      await api.callStandardApi("editNotaBorradorOperacion", { showLoading: true }, props.idBorradorOperacion, {
        IdNota: props.model?.Id!,
        Contenido: responseModal.value,
      });
      emit("update:nota");
    } catch (error) {
      toastError(intl.i18n.t("borradores-operacion.form.editar_nota_error"));
    }
  }
};
</script>

<style lang="scss" scoped>

.nota-card {
  background-color: var(--background-default-default);
  border: var(--border-width-sm) solid var(--border-default-default);
  box-shadow: 0px 2px 6px 0px rgba(14, 20, 26, 0.06);
  border-radius: var(--border-radius-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-xs);
  font-family: var(--font-family-body);

  &-owner {
    border-color: var(--border-primary-default);
    border-left-width: 3px;
  }

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  &-author {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);

    &-avatar {
      width: var(--icon-size-md);
      height: var(--icon-size-md);
      background-color: var(--background-primary-light);
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      color: var(--text-primary-default);
      font-size: 9px;
      font-weight: var(--font-weight-bold);
    }

    &-name {
      color: var(--text-primary-default);
      font-size: var(--font-size-body-md);
      font-weight: var(--font-weight-bold);
    }
  }

  &-actions {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
  }

  &-created-at {
    color: var(--text-default-caption);
    font-size: var(--font-size-body-sm);
  }

  &-btn-edit {
    display: flex;
    align-items: center;
    gap: var(--spacing-2xs);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    color: var(--text-secondary-default);

    .edit-label {
      font-size: var(--font-size-body-sm);
    }

    .dx-icon-edit {
      font-size: var(--font-size-body-md);
    }
  }

  &-body {
    color: var(--text-default-default);
    font-size: var(--font-size-body-md);
    line-height: var(--line-height-body-md);
    white-space: pre-wrap;
  }

  &-footer {
    display: flex;
    align-items: center;
  }

  &-edited-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--text-default-caption);
    font-size: var(--font-size-body-sm);

    .dot {
      width: 5px;
      height: 5px;
      background-color: var(--background-primary-light);
      border-radius: 50%;
    }
  }
}
</style>
